/**
 * Shared chart utilities for rebate forecasting components
 * Clean rewrite to preserve all prediction points without data loss
 */

import { RebatePoint, UnifiedForecastResult } from '@erp/shared-types';
import { formatFullDateDisplay, formatMonthDisplay } from './dateUtils';
import { getRebateAmount } from './rebateAmountSelector';

/**
 * Individual chart data point - one per data type per day
 */
export interface ChartPoint {
  day: number; // daysFromStart for sorting
  date: Date; // actual date for this point
  dateStr: string; // formatted for x-axis (e.g., "Jan 30")
  fullDateStr: string; // formatted for tooltip (e.g., "January 30, 2025")
  type: string; // "estimates" | "linear-regression" | "polynomial" etc.
  value: number; // the rebate amount (not optional)
}

/**
 * Processed forecast data ready for chart rendering
 */
export interface ProcessedForecastData {
  points: ChartPoint[]; // ALL data points (estimates + all predictions)
  types: string[]; // ["estimates", "linear-regression", etc.]
  totals: {
    actualToDate: number; // sum of estimates so far
    projectedTotal: number; // final prediction value
    lastActualDay: number; // where estimates end
  };
  dateRange: {
    start: Date;
    end: Date;
  };
}

/**
 * Recharts-compatible data structure
 */
export interface RechartsDataPoint {
  day: number;
  dateStr: string;
  fullDateStr: string;
  [dataType: string]: number | string; // estimates, linear-regression, etc.
}

export const transformForecastData = (
  forecast: UnifiedForecastResult,
  isCumulative = false,
  predictionSplitDay?: number | null,
  reportedSplitDay?: number | null,
): ProcessedForecastData => {
  const hasMultipleYears = detectMultipleYears(forecast);

  const points: ChartPoint[] = [];
  const types: string[] = [];

  addEstimatePoints(points, types, forecast, hasMultipleYears, isCumulative);
  addPredictionPoints(
    points,
    types,
    forecast,
    hasMultipleYears,
    isCumulative,
    predictionSplitDay,
  );
  // Reported uses its own split day (based on today's date) rather than
  // predictionSplitDay (based on where estimates end). This ensures reported
  // data for past dates always shows as black even when there are no estimates.
  addReportedPoints(
    points,
    types,
    forecast,
    hasMultipleYears,
    isCumulative,
    reportedSplitDay,
  );

  addBaselinePoints(points, types, forecast, hasMultipleYears);

  logChartDebugInfo(points, types);

  const totals = computeTotals(
    points,
    isCumulative,
    forecast.reportedAlgorithm,
  );
  const dateRange = inferDateRange(points, forecast);

  return { points, types, totals, dateRange };
};

function detectMultipleYears(forecast: UnifiedForecastResult): boolean {
  const allDates: Date[] = [];

  const toDate = (d: Date | string): Date => {
    return d instanceof Date ? d : new Date(d);
  };

  forecast.estimates?.forEach((e) => allDates.push(toDate(e.date)));
  if (forecast.predictions) {
    Object.values(forecast.predictions).forEach((predPoints) =>
      predPoints.forEach((p) => allDates.push(toDate(p.date))),
    );
  }
  forecast.reported?.forEach((r) => allDates.push(toDate(r.date)));

  const years = new Set(allDates.map((d) => d.getFullYear()));
  return years.size > 1;
}

function addEstimatePoints(
  points: ChartPoint[],
  types: string[],
  forecast: UnifiedForecastResult,
  hasMultipleYears: boolean,
  isCumulative: boolean,
) {
  if (!forecast.estimates?.length) return;

  // Backend handles all zero-filling - just map the data directly
  const estimatePoints: ChartPoint[] = forecast.estimates.map((e) => ({
    day: e.daysFromStart,
    date: e.date,
    dateStr: formatMonthDisplay(e.date, hasMultipleYears),
    fullDateStr: formatFullDateDisplay(e.date),
    type: 'estimates' as const,
    value: getRebateAmount(e, isCumulative),
  }));

  if (!types.includes('estimates')) types.push('estimates');
  points.push(...estimatePoints);
}

function addPredictionPoints(
  points: ChartPoint[],
  types: string[],
  forecast: UnifiedForecastResult,
  hasMultipleYears: boolean,
  isCumulative: boolean,
  predictionSplitDay?: number | null,
) {
  if (!forecast.predictions) return;

  const toDate = (d: Date | string): Date => {
    return d instanceof Date ? d : new Date(d);
  };

  // Backend handles all zero-filling - just map the data directly
  Object.entries(forecast.predictions).forEach(
    ([method, predPoints]: [string, RebatePoint[]]) => {
      if (!predPoints?.length) return;

      if (predictionSplitDay !== null && predictionSplitDay !== undefined) {
        const pastType = `${method}-past`;
        const pastPoints: ChartPoint[] = [];
        const futurePoints: ChartPoint[] = [];
        let connectionPoint: ChartPoint | null = null;

        predPoints.forEach((p) => {
          const isPast = p.daysFromStart <= predictionSplitDay;
          const chartPoint: ChartPoint = {
            day: p.daysFromStart,
            date: p.date,
            dateStr: formatMonthDisplay(toDate(p.date), hasMultipleYears),
            fullDateStr: formatFullDateDisplay(toDate(p.date)),
            type: isPast ? pastType : method,
            value: getRebateAmount(p, isCumulative),
          };

          if (isPast) {
            pastPoints.push(chartPoint);
            // Track the point at the split day (this will be our connection point)
            if (p.daysFromStart === predictionSplitDay) {
              connectionPoint = chartPoint;
            }
          } else {
            futurePoints.push(chartPoint);
          }
        });

        // If we didn't find an exact match at split day, use the last past point
        if (!connectionPoint && pastPoints.length > 0) {
          connectionPoint = pastPoints[pastPoints.length - 1];
        }

        // Add connection point: duplicate the last past point for the future line
        // This ensures both lines share a point at the split, creating a visual connection
        if (connectionPoint && futurePoints.length > 0) {
          const futureConnectionPoint: ChartPoint = {
            ...connectionPoint,
            type: method, // Use original method name for future
          };
          futurePoints.unshift(futureConnectionPoint);
        }

        if (pastPoints.length > 0) {
          if (!types.includes(pastType)) types.push(pastType);
          points.push(...pastPoints);
        }

        if (futurePoints.length > 0) {
          if (!types.includes(method)) types.push(method);
          points.push(...futurePoints);
        }
      } else {
        predPoints.forEach((p) => {
          points.push({
            day: p.daysFromStart,
            date: p.date,
            dateStr: formatMonthDisplay(toDate(p.date), hasMultipleYears),
            fullDateStr: formatFullDateDisplay(toDate(p.date)),
            type: method,
            value: getRebateAmount(p, isCumulative),
          });
        });

        if (!types.includes(method)) types.push(method);
      }
    },
  );
}

function addReportedPoints(
  points: ChartPoint[],
  types: string[],
  forecast: UnifiedForecastResult,
  hasMultipleYears: boolean,
  isCumulative: boolean,
  predictionSplitDay?: number | null,
) {
  if (!forecast.reported?.length) return;

  const toDate = (d: Date | string): Date => {
    return d instanceof Date ? d : new Date(d);
  };

  if (predictionSplitDay === null || predictionSplitDay === undefined) {
    const reportedPoints: ChartPoint[] = forecast.reported.map((r) => {
      const date = toDate(r.date);
      return {
        day: r.daysFromStart,
        date,
        dateStr: formatMonthDisplay(date, hasMultipleYears),
        fullDateStr: formatFullDateDisplay(date),
        type: 'reported' as const,
        value: getRebateAmount(r, isCumulative),
      };
    });
    if (!types.includes('reported')) types.push('reported');
    points.push(...reportedPoints);
    return;
  }

  const pastReportedPoints: ChartPoint[] = [];
  const futureReportedPoints: ChartPoint[] = [];
  let connectionPoint: ChartPoint | null = null;

  forecast.reported.forEach((r) => {
    const date = toDate(r.date);
    const isPast = r.daysFromStart <= predictionSplitDay;

    const chartPoint: ChartPoint = {
      day: r.daysFromStart,
      date,
      dateStr: formatMonthDisplay(date, hasMultipleYears),
      fullDateStr: formatFullDateDisplay(date),
      type: isPast ? ('reported' as const) : ('reported_future' as const),
      value: getRebateAmount(r, isCumulative),
    };

    if (isPast) {
      pastReportedPoints.push(chartPoint);
      // Track the point at the split day (this will be our connection point)
      if (r.daysFromStart === predictionSplitDay) {
        connectionPoint = chartPoint;
      }
    } else {
      futureReportedPoints.push(chartPoint);
    }
  });

  // If we didn't find an exact match at split day, use the last past point
  if (!connectionPoint && pastReportedPoints.length > 0) {
    connectionPoint = pastReportedPoints[pastReportedPoints.length - 1];
  }

  // Add connection point: duplicate the last past point for the future line
  // This ensures both lines share a point at the split, creating a visual connection
  if (connectionPoint && futureReportedPoints.length > 0) {
    const futureConnectionPoint: ChartPoint = {
      ...connectionPoint,
      type: 'reported_future' as const,
    };
    // Insert at the beginning of future points so it connects to the last past point
    futureReportedPoints.unshift(futureConnectionPoint);
  }

  if (pastReportedPoints.length > 0) {
    if (!types.includes('reported')) types.push('reported');
    points.push(...pastReportedPoints);
  }

  if (futureReportedPoints.length > 0) {
    if (!types.includes('reported_future')) types.push('reported_future');
    points.push(...futureReportedPoints);
  }
}

function addBaselinePoints(
  points: ChartPoint[],
  types: string[],
  forecast: UnifiedForecastResult,
  hasMultipleYears: boolean,
) {
  if (!types.length) return;

  const periodStartDate =
    forecast.forecastPeriod?.startDate ||
    (points.length > 0
      ? new Date(Math.min(...points.map((p) => p.date.getTime())))
      : new Date());

  const baselineDay = 0; // Day 0 is the start of the forecast period

  // Determine which prediction strategies are in "split mode"
  // Split mode = both past and future lines exist for a strategy
  const splitPredictionFutureTypes = new Set<string>();
  types.forEach((type) => {
    if (type.endsWith('-past')) {
      const futureType = type.replace(/-past$/, '');
      if (types.includes(futureType)) {
        splitPredictionFutureTypes.add(futureType);
      }
    }
  });

  // Determine if we're in "split mode" for reported
  // Split mode = both past (black) and future (blue) reported lines exist
  const isInReportedSplitMode =
    types.includes('reported') && types.includes('reported_future');

  // Pre-compute whether the first month of the period has real reported data.
  // Used below to decide if reported lines get a day-0 baseline anchor.
  const toDate = (d: Date | string): Date =>
    d instanceof Date ? d : new Date(d);
  const periodStart = toDate(periodStartDate);
  const firstMonthYear = periodStart.getUTCFullYear();
  const firstMonth = periodStart.getUTCMonth();
  const hasFirstMonthReported = points.some((p) => {
    if (p.type !== 'reported' && p.type !== 'reported_future') return false;
    if (p.day <= baselineDay) return false;
    const d = toDate(p.date);
    return (
      d.getUTCFullYear() === firstMonthYear && d.getUTCMonth() === firstMonth
    );
  });

  types.forEach((type) => {
    // Skip reported_future ONLY if we have black reported points to connect to
    if (type === 'reported_future' && isInReportedSplitMode) return;

    // For reported lines, only add a day-0 baseline if the first month already
    // has real reported data (e.g. a Jan 31 point). Without this we'd draw an
    // artificial $0 dot at Jan 1 when nothing was reported for January.
    if (type === 'reported' || type === 'reported_future') {
      if (!hasFirstMonthReported) return;
    }

    // In prediction split mode:
    // - future types should NOT get a day 0 baseline - they start at the split point
    // - past types SHOULD get a day 0 baseline
    // In non-split mode:
    // - the strategy type should get a day 0 baseline (it's the full line)
    if (splitPredictionFutureTypes.has(type)) return;

    const hasBaselinePoint = points.some(
      (p) => p.type === type && p.day === baselineDay,
    );
    if (!hasBaselinePoint) {
      points.push({
        day: baselineDay,
        date: periodStartDate,
        dateStr: formatMonthDisplay(periodStartDate, hasMultipleYears),
        fullDateStr: formatFullDateDisplay(periodStartDate),
        type,
        value: 0,
      });
    }
  });
}

// NOTE: params _points/_types currently unused (kept via '_' prefix) — revisit and delete if not needed
function logChartDebugInfo(_points: ChartPoint[], _types: string[]) {
  if (process.env.NODE_ENV !== 'development') return;
}

function computeTotals(
  points: ChartPoint[],
  isCumulative: boolean,
  preferredAlgorithm?: string,
) {
  const estimates = points.filter((p) => p.type === 'estimates');

  // For cumulative mode: use last point's value (already cumulative)
  // For incremental mode: sum all incremental values
  const actualToDate = estimates.length
    ? isCumulative
      ? estimates.reduce((latest, p) => (p.day > latest.day ? p : latest)).value
      : estimates.reduce((sum, p) => sum + p.value, 0)
    : 0;

  const lastActualDay = estimates.length
    ? Math.max(...estimates.map((p) => p.day))
    : 0;

  let projectedTotal = actualToDate;
  const preds = points.filter(
    (p) =>
      p.type !== 'estimates' &&
      p.type !== 'reported' &&
      p.type !== 'reported_future',
  );
  if (preds.length) {
    // Group by base method (strip '-past' suffix so past+future points are grouped together)
    const byMethod = new Map<string, ChartPoint[]>();
    preds.forEach((p) => {
      const method = p.type.replace(/-past$/, '');
      const arr = byMethod.get(method) ?? [];
      arr.push(p);
      byMethod.set(method, arr);
    });

    const methods = Array.from(byMethod.keys());
    const preferred = preferredAlgorithm ?? 'linear-regression';
    const methodToUse = methods.includes(preferred) ? preferred : methods[0];
    const methodPoints = byMethod.get(methodToUse) || [];
    if (methodPoints.length) {
      // Deduplicate by day (split mode can produce both "method-past" and "method" for the connection day)
      const uniqueByDay = Array.from(
        new Map(methodPoints.map((p) => [p.day, p])).values(),
      );
      // For cumulative mode: use last point's value (already cumulative)
      // For incremental mode: sum all incremental values
      if (isCumulative) {
        const lastPoint = uniqueByDay.reduce((latest, p) =>
          p.day > latest.day ? p : latest,
        );
        projectedTotal = lastPoint.value;
      } else {
        projectedTotal = uniqueByDay.reduce((sum, p) => sum + p.value, 0);
      }
    }
  }

  return { actualToDate, projectedTotal, lastActualDay };
}

function inferDateRange(points: ChartPoint[], forecast: UnifiedForecastResult) {
  const pointDates = points.map((p) => p.date);
  return {
    start:
      forecast.forecastPeriod?.startDate ||
      new Date(Math.min(...pointDates.map((d) => d.getTime()))),
    end:
      forecast.forecastPeriod?.endDate ||
      new Date(Math.max(...pointDates.map((d) => d.getTime()))),
  };
}

/**
 * Convert ProcessedForecastData to Recharts-compatible format
 */
export const convertToRechartsData = (
  processedData: ProcessedForecastData,
): RechartsDataPoint[] => {
  const dayMap = new Map<number, RechartsDataPoint>();

  processedData.points.forEach((point) => {
    if (!dayMap.has(point.day)) {
      dayMap.set(point.day, {
        day: point.day,
        dateStr: point.dateStr,
        fullDateStr: point.fullDateStr,
      });
    }
    const dataPoint = dayMap.get(point.day);
    if (dataPoint) {
      dataPoint[point.type] = point.value;
    }
  });

  const rechartsData = Array.from(dayMap.values()).sort(
    (a, b) => a.day - b.day,
  );

  return rechartsData;
};

/**
 * Get the most recent actual position for display
 * Returns a proper RebatePoint from the original forecast data
 */
export const getCurrentActualPosition = <T extends { daysFromStart: number }>(
  estimates: T[],
): T | null => {
  if (!estimates?.length) return null;

  // Sort by daysFromStart and get the last one
  const sortedEstimates = [...estimates].sort(
    (a, b) => a.daysFromStart - b.daysFromStart,
  );
  return sortedEstimates[sortedEstimates.length - 1];
};

/**
 * Standard chart formatting utilities
 */
export const chartFormatters = {
  /**
   * Format Y-axis ticks with currency notation and consistent spacing
   */
  formatYAxis: (value: number): string => {
    if (value === 0) return '$0';

    if (value >= 1000000) {
      const millions = value / 1000000;
      return '$' + millions.toFixed(1) + 'M';
    } else if (value >= 1000) {
      const thousands = value / 1000;
      return '$' + Math.round(thousands) + 'K';
    } else {
      return '$' + Math.round(value);
    }
  },

  /**
   * Format X-axis ticks using chart data
   */
  formatXAxis: (value: number, chartData: RechartsDataPoint[]): string => {
    const dataPoint = chartData.find((d) => d.day === value);
    return dataPoint ? dataPoint.dateStr : value.toString();
  },
};

/**
 * Standard chart configuration objects
 */
export const chartConfig = {
  /**
   * Standard margin settings for forecast charts
   */
  chartMargin: { top: 20, right: 30, left: 20, bottom: 60 },

  /**
   * Standard height for forecast charts
   */
  chartHeight: 600,

  /**
   * Standard legend configuration
   */
  legendStyle: {
    paddingTop: '20px',
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
  },

  /**
   * Y-axis domain calculation for better visualization
   */
  getYAxisDomain: (): [number, (dataMax: number) => number] => [
    0, // Always start Y-axis at 0
    (dataMax: number) => dataMax * 1.02,
  ],

  /**
   * Calculate 4 clean, evenly-spaced Y-axis ticks
   * Handles both positive-only and negative values
   */
  getYAxisTicks: (minValue: number, maxValue: number): number[] => {
    if (maxValue === 0 && minValue === 0) return [0];

    // If there are negative values, calculate ticks spanning the full range
    if (minValue < 0) {
      const range = maxValue - minValue;
      // Use one magnitude smaller for tighter bounds
      const magnitude = Math.pow(
        10,
        Math.floor(Math.log10(Math.abs(range))) - 1,
      );
      const normalizedRange = range / magnitude;
      const roundedRange = Math.ceil(normalizedRange) * magnitude;

      // Calculate step size
      const step = roundedRange / 4;

      // Start from rounded min value
      const roundedMin = Math.floor(minValue / magnitude) * magnitude;
      return [
        roundedMin,
        roundedMin + step,
        roundedMin + step * 2,
        roundedMin + step * 3,
        roundedMin + roundedRange,
      ];
    }

    // Use one magnitude smaller for tighter bounds
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)) - 1);
    const normalized = maxValue / magnitude;
    const roundedMax = Math.ceil(normalized) * magnitude;

    const step = roundedMax / 4;
    return [0, step, step * 2, step * 3, roundedMax];
  },
};
